import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { authSignUp, checkEmailExists, checkMobileExists } from '../../lib/auth';
import PlatformSelector from './PlatformSelector';

const COUNTRIES = [
  'Afghanistan','Albania','Algeria','Argentina','Australia','Austria','Bangladesh','Belgium',
  'Brazil','Canada','Chile','China','Colombia','Croatia','Czech Republic','Denmark','Egypt',
  'Ethiopia','Finland','France','Germany','Ghana','Greece','Hungary','India','Indonesia',
  'Iran','Iraq','Ireland','Israel','Italy','Japan','Jordan','Kenya','Malaysia','Mexico',
  'Morocco','Netherlands','New Zealand','Nigeria','Norway','Pakistan','Peru','Philippines',
  'Poland','Portugal','Romania','Russia','Saudi Arabia','Singapore','South Africa',
  'South Korea','Spain','Sri Lanka','Sweden','Switzerland','Thailand','Turkey','Ukraine',
  'United Arab Emirates','United Kingdom','United States','Vietnam','Other',
];

const VOLUME_OPTIONS = [
  '1–50 orders/month','51–200 orders/month','201–500 orders/month',
  '501–1000 orders/month','1000+ orders/month',
];

const validateMobile = (mobile) => {
  const mobileRegex = /^\+\d{1,4}[-\s]?\d{6,14}$/;
  return mobileRegex.test(mobile.replace(/\s/g, ''));
};

// ─── Success Screen (email confirmation only, no mobile OTP) ──────────────────
const SuccessScreen = ({ email }) => (
  <div className="min-h-screen flex items-center justify-center bg-gray-100 px-4 py-8">
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="max-w-md w-full bg-white rounded-2xl shadow-xl overflow-hidden"
    >
      <div className="bg-green-600 p-8 text-white text-center">
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: 'spring', delay: 0.2 }}
          className="inline-flex items-center justify-center w-20 h-20 bg-white/20 rounded-full mb-4"
        >
          <SafeIcon icon={FiIcons.FiCheckCircle} className="text-4xl" />
        </motion.div>
        <h2 className="text-2xl font-bold">Account Created!</h2>
        <p className="text-green-100 mt-1 text-sm">One last step — verify your email</p>
      </div>

      <div className="p-6 space-y-4">
        {/* Email confirmation card */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start gap-3">
          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
            <SafeIcon icon={FiIcons.FiMail} className="text-blue-600 text-lg" />
          </div>
          <div>
            <p className="text-sm font-bold text-blue-800">Check Your Email</p>
            <p className="text-xs text-blue-600 mt-0.5">We've sent a confirmation link to:</p>
            <p className="text-sm font-mono font-bold text-blue-700 mt-1 break-all">{email}</p>
            <p className="text-xs text-blue-600 mt-1.5">
              Click the link in the email to verify and activate your account.
            </p>
          </div>
        </div>

        {/* Next steps */}
        <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
          <p className="text-xs font-bold text-gray-600 uppercase tracking-wider mb-2 flex items-center gap-1.5">
            <SafeIcon icon={FiIcons.FiInfo} className="text-gray-400" /> Next Steps
          </p>
          <ol className="space-y-1.5">
            {['Open your email inbox', 'Click the confirmation link', 'Return here and sign in'].map((step, i) => (
              <li key={i} className="flex items-center gap-2 text-xs text-gray-600">
                <span className="w-5 h-5 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center flex-shrink-0">
                  {i + 1}
                </span>
                {step}
              </li>
            ))}
          </ol>
        </div>

        <Link
          to="/login"
          className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 rounded-xl shadow transition-all flex items-center justify-center gap-2"
        >
          <SafeIcon icon={FiIcons.FiLogIn} /> Go to Sign In
        </Link>

        <p className="text-center text-xs text-gray-400">
          Didn't receive the email?{' '}
          <span className="text-blue-500 font-semibold">Check your spam folder.</span>
        </p>
      </div>
    </motion.div>
  </div>
);

// ─── Field Error ──────────────────────────────────────────────────────────────
const FieldError = ({ message, action, actionHref }) => (
  <motion.div
    initial={{ opacity: 0, y: -4 }}
    animate={{ opacity: 1, y: 0 }}
    className="mt-1.5 flex items-start gap-1.5 text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-2.5 py-1.5"
  >
    <SafeIcon icon={FiIcons.FiAlertCircle} className="flex-shrink-0 mt-0.5" />
    <span>
      {message}
      {action && actionHref && (
        <> <Link to={actionHref} className="font-bold underline">{action}</Link></>
      )}
    </span>
  </motion.div>
);

// ─── Input Field ──────────────────────────────────────────────────────────────
const InputField = ({ label, icon, required: isRequired, fieldError, ...props }) => (
  <div>
    <label className="block text-sm font-semibold text-gray-700 mb-1">
      {label} {isRequired && <span className="text-red-500">*</span>}
    </label>
    <div className="relative">
      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
        <SafeIcon icon={icon} />
      </div>
      <input
        {...props}
        required={isRequired}
        className={`block w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 outline-none transition-colors ${
          fieldError
            ? 'border-red-400 bg-red-50 focus:ring-red-300'
            : 'border-gray-300 focus:ring-blue-500'
        }`}
      />
    </div>
    {fieldError && <FieldError message={fieldError} />}
  </div>
);

// ─── Main Signup Component ────────────────────────────────────────────────────
const Signup = () => {
  const [step, setStep] = useState(1);
  const [signedUp, setSignedUp] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [form, setForm] = useState({
    fullName: '', email: '', password: '', mobile: '',
    country: '', monthlyVolume: '', platforms: [],
  });
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});

  if (signedUp) return <SuccessScreen email={registeredEmail} />;

  const set = (key) => (e) => {
    setForm(f => ({ ...f, [key]: e.target.value }));
    if (fieldErrors[key]) setFieldErrors(fe => ({ ...fe, [key]: null }));
    setError(null);
  };

  // ── Step 1: validate then check uniqueness ──────────────────────────────────
  const handleStep1 = async (e) => {
    e.preventDefault();
    const errs = {};

    if (!form.fullName.trim()) errs.fullName = 'Full name is required.';
    if (form.password.length < 6) errs.password = 'Password must be at least 6 characters.';
    if (!form.mobile.trim()) {
      errs.mobile = 'Mobile number is required.';
    } else if (!validateMobile(form.mobile.trim())) {
      errs.mobile = 'Enter a valid mobile with country code (e.g. +91-9876543210).';
    }

    if (Object.keys(errs).length > 0) {
      setFieldErrors(errs);
      return;
    }

    setChecking(true);
    setFieldErrors({});
    setError(null);

    try {
      const [emailTaken, mobileTaken] = await Promise.all([
        checkEmailExists(form.email.trim()),
        checkMobileExists(form.mobile.trim()),
      ]);

      const newErrs = {};
      if (emailTaken) newErrs.email = 'An account with this email already exists.';
      if (mobileTaken) newErrs.mobile = 'This mobile number is already registered.';

      if (Object.keys(newErrs).length > 0) {
        setFieldErrors(newErrs);
        setChecking(false);
        return;
      }
    } catch (err) {
      console.warn('Uniqueness check failed:', err);
      // Don't block the user if the check itself fails
    }

    setChecking(false);
    setStep(2);
  };

  // ── Step 2: submit ──────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.country) { setError('Please select your country.'); return; }
    if (!form.monthlyVolume) { setError('Please select monthly order volume.'); return; }
    if (form.platforms.length === 0) { setError('Please select at least one selling platform.'); return; }

    setLoading(true);
    setError(null);

    const { data, error: signupError } = await authSignUp(form);

    if (signupError) {
      const msg = signupError.message || '';

      if (msg === 'DUPLICATE_EMAIL') {
        // Go back to step 1 and highlight the email field
        setFieldErrors({ email: 'An account with this email already exists.' });
        setStep(1);
      } else if (msg === 'DUPLICATE_MOBILE') {
        setFieldErrors({ mobile: 'This mobile number is already registered.' });
        setStep(1);
      } else {
        setError(msg || 'Signup failed. Please try again.');
      }

      setLoading(false);
      return;
    }

    if (data?.user) {
      setRegisteredEmail(form.email.trim());
      setSignedUp(true);
    } else {
      setError('Signup failed. Please try again.');
    }

    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 px-4 py-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-md w-full bg-white rounded-2xl shadow-xl overflow-hidden"
      >
        {/* Header */}
        <div className="bg-blue-600 p-6 text-white text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 bg-white/20 rounded-full mb-3">
            <SafeIcon icon={FiIcons.FiUserPlus} className="text-2xl" />
          </div>
          <h2 className="text-2xl font-bold">Create Account</h2>
          <p className="text-blue-100 mt-1 text-sm">Start recording your shipments</p>
          <div className="flex items-center justify-center mt-4 space-x-2">
            {[1, 2].map(s => (
              <div
                key={s}
                className={`h-2 rounded-full transition-all duration-300 ${
                  s === step ? 'w-8 bg-white' : 'w-4 bg-white/40'
                }`}
              />
            ))}
          </div>
          <p className="text-xs text-blue-100 mt-2">Step {step} of 2</p>
        </div>

        <div className="p-6">
          <AnimatePresence>
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="mb-4 p-3 bg-red-50 border-l-4 border-red-500 text-red-700 text-sm flex items-start gap-2 rounded-r-lg"
              >
                <SafeIcon icon={FiIcons.FiAlertCircle} className="flex-shrink-0 mt-0.5" />
                <span>{error}</span>
              </motion.div>
            )}
          </AnimatePresence>

          {/* ── Step 1 ── */}
          {step === 1 ? (
            <form onSubmit={handleStep1} className="space-y-4">
              <InputField
                label="Full Name" icon={FiIcons.FiUser} type="text"
                value={form.fullName} onChange={set('fullName')}
                placeholder="Your full name" required
                fieldError={fieldErrors.fullName}
              />

              {/* Email */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  Email Address <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
                    <SafeIcon icon={FiIcons.FiMail} />
                  </div>
                  <input
                    type="email" value={form.email} onChange={set('email')} required
                    placeholder="name@company.com"
                    className={`block w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 outline-none transition-colors ${
                      fieldErrors.email
                        ? 'border-red-400 bg-red-50 focus:ring-red-300'
                        : 'border-gray-300 focus:ring-blue-500'
                    }`}
                  />
                </div>
                {fieldErrors.email && (
                  <FieldError
                    message={fieldErrors.email}
                    action="Sign in instead"
                    actionHref="/login"
                  />
                )}
              </div>

              {/* Mobile */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  Mobile Number <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
                    <SafeIcon icon={FiIcons.FiPhone} />
                  </div>
                  <input
                    type="tel" value={form.mobile} onChange={set('mobile')} required
                    placeholder="+91-9876543210"
                    className={`block w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 outline-none transition-colors ${
                      fieldErrors.mobile
                        ? 'border-red-400 bg-red-50 focus:ring-red-300'
                        : 'border-gray-300 focus:ring-blue-500'
                    }`}
                  />
                </div>
                {fieldErrors.mobile ? (
                  <FieldError message={fieldErrors.mobile} />
                ) : (
                  <div className="mt-1.5 bg-blue-50 border border-blue-100 rounded-lg px-3 py-2">
                    <p className="text-xs text-blue-700 font-semibold mb-0.5 flex items-center gap-1">
                      <SafeIcon icon={FiIcons.FiInfo} className="text-blue-500" />
                      Format: Country Code + Number
                    </p>
                    <p className="text-xs text-blue-600">
                      🇮🇳 India: <span className="font-mono font-bold">+91-9876543210</span>
                    </p>
                    <p className="text-xs text-blue-500 mt-0.5">
                      🇺🇸 USA: <span className="font-mono">+1-4155552671</span>
                      &nbsp;|&nbsp;
                      🇬🇧 UK: <span className="font-mono">+44-7911123456</span>
                    </p>
                  </div>
                )}
              </div>

              {/* Password */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  Password <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
                    <SafeIcon icon={FiIcons.FiLock} />
                  </div>
                  <input
                    type="password" value={form.password} onChange={set('password')}
                    placeholder="Min. 6 characters" minLength={6} required
                    className={`block w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 outline-none transition-colors ${
                      fieldErrors.password
                        ? 'border-red-400 bg-red-50 focus:ring-red-300'
                        : 'border-gray-300 focus:ring-blue-500'
                    }`}
                  />
                </div>
                {fieldErrors.password && <FieldError message={fieldErrors.password} />}
              </div>

              <button
                type="submit"
                disabled={checking}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 rounded-lg shadow transition-all flex items-center justify-center disabled:opacity-60"
              >
                {checking ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                    Checking availability…
                  </>
                ) : (
                  <>Continue <SafeIcon icon={FiIcons.FiArrowRight} className="ml-2" /></>
                )}
              </button>
            </form>
          ) : (

          /* ── Step 2 ── */
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Country</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
                    <SafeIcon icon={FiIcons.FiGlobe} />
                  </div>
                  <select value={form.country} onChange={set('country')} required
                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none bg-white text-gray-700">
                    <option value="">Select your country</option>
                    {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Monthly Order Volume</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
                    <SafeIcon icon={FiIcons.FiTrendingUp} />
                  </div>
                  <select value={form.monthlyVolume} onChange={set('monthlyVolume')} required
                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none bg-white text-gray-700">
                    <option value="">Select volume range</option>
                    {VOLUME_OPTIONS.map(v => <option key={v} value={v}>{v}</option>)}
                  </select>
                </div>
              </div>

              <PlatformSelector
                selected={form.platforms}
                onChange={(platforms) => setForm(f => ({ ...f, platforms }))}
              />

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => { setStep(1); setError(null); }}
                  className="flex-1 py-3 border border-gray-300 text-gray-700 font-bold rounded-lg hover:bg-gray-50 transition-all flex items-center justify-center"
                >
                  <SafeIcon icon={FiIcons.FiArrowLeft} className="mr-2" /> Back
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 rounded-lg shadow transition-all flex items-center justify-center disabled:opacity-50"
                >
                  {loading
                    ? <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    : 'Sign Up'}
                </button>
              </div>
            </form>
          )}

          <div className="mt-4 text-center text-sm text-gray-600">
            Already have an account?{' '}
            <Link to="/login" className="text-blue-600 font-bold hover:underline">Sign in</Link>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default Signup;